// Spec 28 PR 1 — RepoPickerCard.
//
// Signature element: group headers fade in first, then rows
// cascade in beneath each group with a small stagger. Selected
// rows pick up a soft accent border; the confirm pill counts the
// selection live ("Ingest selected (6)").

import { ArrowRight, Check, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Pill } from "@/components/glass";

import type { WidgetRenderProps } from "../registry";

interface Repo {
	id: string;
	name: string;
	description?: string | null;
	stars?: number | null;
	lastPushedAt?: string | null;
	suggested?: boolean;
}

interface Group {
	groupId: string;
	label: string;
	repos: Repo[];
}

interface RepoPickerCardProps {
	title: string;
	body?: string | null;
	groups: Group[];
	minPicks: number;
	maxPicks: number;
	confirmLabel: string;
	cancelLabel: string | null;
}

export function RepoPickerCard({
	props,
	state,
	onAction,
}: WidgetRenderProps<RepoPickerCardProps>) {
	const submitting = state.kind === "submitting";
	const succeeded = state.kind === "succeeded";
	const failed = state.kind === "failed";

	const initiallySelected = useMemo(() => {
		const s = new Set<string>();
		for (const g of props.groups) {
			for (const r of g.repos) {
				if (r.suggested) s.add(r.id);
			}
		}
		return s;
	}, [props.groups]);
	const [selected, setSelected] = useState<Set<string>>(initiallySelected);

	useEffect(() => {
		setSelected(initiallySelected);
	}, [initiallySelected]);

	if (succeeded) {
		const r = state.result as { linked?: number } | undefined;
		const count = r?.linked ?? selected.size;
		return (
			<div className="text-center py-2">
				<span className="text-[12px] text-[var(--color-text-muted)]">
					<span className="text-[var(--color-success)] mr-1.5">✓</span>
					{count} {count === 1 ? "repo" : "repos"} queued for ingest
				</span>
			</div>
		);
	}

	const toggle = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else if (next.size < props.maxPicks) {
				next.add(id);
			}
			return next;
		});
	};

	const submit = () => {
		if (submitting || selected.size < props.minPicks) return;
		onAction("confirm", { repoIds: Array.from(selected) });
	};

	return (
		<div
			className="py-8 space-y-6"
			style={{ opacity: submitting ? 0.8 : 1, transition: "opacity 200ms" }}
		>
			<div className="text-center space-y-2">
				<div className="text-[24px] font-medium tracking-tight text-[var(--color-text)] anim-fade-rise">
					{props.title}
				</div>
				{props.body && (
					<div
						className="text-[13px] text-[var(--color-text-muted)] max-w-md mx-auto leading-relaxed anim-fade-in"
						style={{ animationDelay: "120ms" }}
					>
						{props.body}
					</div>
				)}
			</div>

			<div className="max-w-[560px] mx-auto space-y-5">
				{props.groups.map((group, gi) => (
					<div
						key={group.groupId}
						className="space-y-2 anim-fade-in"
						style={{ animationDelay: `${200 + gi * 80}ms` }}
					>
						<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] px-1">
							{group.label}
						</div>
						<div className="space-y-1">
							{group.repos.map((repo, ri) => (
								<RepoRow
									key={repo.id}
									repo={repo}
									selected={selected.has(repo.id)}
									onToggle={() => toggle(repo.id)}
									delayMs={Math.min(gi * 80 + ri * 30, 600)}
									disabled={submitting}
								/>
							))}
							{group.repos.length === 0 && (
								<div className="text-[11px] text-[var(--color-text-dim)] italic px-3">
									No repos in this group.
								</div>
							)}
						</div>
					</div>
				))}
			</div>

			{failed && (
				<div className="text-center text-[11px] text-[var(--color-warning,#f59e0b)]">
					Couldn't queue ingest. Try again or skip.
				</div>
			)}

			<div className="flex flex-col items-center gap-3 pt-2">
				<Pill
					onClick={submit}
					disabled={submitting || selected.size < props.minPicks}
					className="!h-11 !px-5 !text-[14px]"
				>
					{submitting ? (
						<Loader2 className="w-4 h-4 animate-spin" />
					) : (
						<>
							{failed
								? "Try again"
								: selected.size > 0
									? `${props.confirmLabel} (${selected.size})`
									: props.confirmLabel}
							<ArrowRight className="w-4 h-4" />
						</>
					)}
				</Pill>
				{props.cancelLabel && !submitting && (
					<button
						type="button"
						onClick={() => onAction("cancel")}
						className="text-[12px] text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)] transition-colors"
					>
						{props.cancelLabel}
					</button>
				)}
			</div>
		</div>
	);
}

function RepoRow({
	repo,
	selected,
	onToggle,
	delayMs,
	disabled,
}: {
	repo: Repo;
	selected: boolean;
	onToggle: () => void;
	delayMs: number;
	disabled?: boolean;
}) {
	return (
		<button
			type="button"
			onClick={onToggle}
			disabled={disabled}
			className="w-full flex items-start gap-3 px-3 py-2 rounded-md text-left transition-colors disabled:opacity-40 anim-fade-rise"
			style={{
				background: selected ? "rgba(167,139,250,0.06)" : "rgba(255,255,255,0.02)",
				borderWidth: "1px",
				borderStyle: "solid",
				borderColor: selected
					? "rgba(167,139,250,0.35)"
					: "var(--color-border)",
				animationDelay: `${delayMs}ms`,
			}}
		>
			<div
				className="w-4 h-4 mt-0.5 rounded-[3px] flex items-center justify-center shrink-0"
				style={{
					background: selected ? "var(--color-text)" : "transparent",
					borderWidth: "1px",
					borderStyle: "solid",
					borderColor: selected
						? "var(--color-text)"
						: "var(--color-border-hi)",
				}}
			>
				{selected && <Check className="w-3 h-3 text-[var(--color-bg)]" />}
			</div>
			<div className="flex-1 min-w-0">
				<div className="flex items-baseline gap-2">
					<span className="text-[13px] font-medium text-[var(--color-text)] mono truncate">
						{repo.name}
					</span>
					{typeof repo.stars === "number" && repo.stars > 0 && (
						<span className="text-[10px] text-[var(--color-text-dim)] shrink-0">
							{formatStars(repo.stars)}★
						</span>
					)}
				</div>
				{repo.description && (
					<div className="text-[11px] text-[var(--color-text-muted)] truncate leading-snug">
						{repo.description}
					</div>
				)}
			</div>
			{repo.suggested && (
				<span className="text-[9px] uppercase tracking-wider text-[var(--color-text-dim)] shrink-0 mt-1">
					suggested
				</span>
			)}
		</button>
	);
}

function formatStars(n: number): string {
	if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
	return String(n);
}
